import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import { useId, useRef, useState } from "react";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import {
  formatPracticalProgress,
  searchPracticalProgress,
  type PracticalProgress,
  type PracticalProgressCategory,
  type PracticalProgressSkill,
  type PracticalProgressState,
} from "../utils/practicalProgress";

export function StudentPracticalProgressDialog(
  props: {
    studentName: string;
    drivingLicenseClass: string;
    data?: PracticalProgress;
    isError: boolean;
    isFetching: boolean;
    onRetry: () => void;
  } & DialogProps,
) {
  const { studentName, drivingLicenseClass, data, isError, isFetching, onRetry, ...dialogProps } = props;
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));

  return (
    <Dialog
      {...dialogProps}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{ sx: { height: fullScreen ? "100%" : 720 } }}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} sx={{ pr: 8 }}>
        Praktischer Fortschritt
        <Typography component="span" variant="body2" color="text.secondary" display="block">
          {studentName} · {drivingLicenseClass}
          {!isError && data && ` · ${formatPracticalProgress(data.percentage)} Gesamtfortschritt`}
        </Typography>
      </DialogTitle>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <DialogContent ref={contentRef} tabIndex={-1} dividers sx={{ minHeight: 320, pt: 2 }}>
        {isError ? (
          <Alert
            severity="error"
            action={
              <Button onClick={onRetry} disabled={isFetching}>
                Erneut versuchen
              </Button>
            }
          >
            Der praktische Fortschritt konnte nicht geladen werden.
          </Alert>
        ) : !data ? (
          <LinearProgress aria-label="Praktischer Fortschritt wird geladen" />
        ) : (
          <_ProgressContents
            data={data}
            onNavigate={() => {
              if (contentRef.current) {
                contentRef.current.scrollTop = 0;
                contentRef.current.focus();
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function _ProgressContents(props: { data: PracticalProgress; onNavigate: () => void }) {
  const { data, onNavigate } = props;
  const [path, setPath] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const ancestors: PracticalProgressCategory[] = [];
  let nodes = data.nodes;
  let invalidPath = false;
  for (const id of path) {
    const category = nodes.find((node) => node.id === id && node.type === "category");
    if (!category || category.type !== "category") {
      invalidPath = true;
      break;
    }
    ancestors.push(category);
    nodes = category.children;
  }
  const current = ancestors.at(-1);
  const searching = !!search.trim();
  const entries = searching
    ? searchPracticalProgress(nodes, search, ancestors)
    : nodes.map((node) => ({ node, ancestors }));

  function navigate(nextPath: string[]) {
    setPath(nextPath);
    setSearch("");
    onNavigate();
  }

  return (
    <Stack spacing={2}>
      {!!path.length && (
        <Box>
          <Button startIcon={<NavigateBeforeIcon />} onClick={() => navigate(path.slice(0, -1))}>
            Zurück
          </Button>
        </Box>
      )}
      {invalidPath ? (
        <Alert severity="info">Dieser Ausbildungsbereich ist nicht mehr verfügbar. Bitte gehe zurück.</Alert>
      ) : (
        <>
          {current && (
            <Box>
              <Typography variant="h6" component="h2">
                {current.title}
              </Typography>
              {current.description && (
                <Typography variant="body2" color="text.secondary">
                  {current.description}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            label="Ausbildungsinhalte suchen"
            type="search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {entries.length === 0 && (
            <Alert severity="info">
              {searching
                ? "Keine passenden Ausbildungsinhalte gefunden."
                : (current?.emptyMessage ?? "Für diesen Bereich sind keine Ausbildungsinhalte hinterlegt.")}
            </Alert>
          )}
          <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", p: 0, m: 0 }}>
            {entries.map(({ node, ancestors: entryAncestors }) => (
              <Box component="li" key={[...entryAncestors.map((entry) => entry.id), node.id].join("/")}>
                {searching && (
                  <Typography variant="caption" color="text.secondary">
                    {["Übersicht", ...entryAncestors.map((entry) => entry.title)].join(" > ")}
                  </Typography>
                )}
                {node.type === "category" ? (
                  <ButtonBase
                    onClick={() => navigate([...entryAncestors.map((entry) => entry.id), node.id])}
                    aria-label={`${node.title} öffnen`}
                    sx={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "action.hover" },
                      "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main" },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                        <Typography variant="body2" fontWeight="bold">
                          {node.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatPracticalProgress(node.percentage)} Fortschritt
                          {node.description && ` · ${node.description}`}
                        </Typography>
                      </Box>
                      <NavigateNextIcon />
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={node.percentage}
                      aria-label={`Fortschritt: ${node.title}`}
                      sx={{
                        mt: 1.5,
                        height: 6,
                        borderRadius: "999px",
                        bgcolor: "progress.light",
                        "& .MuiLinearProgress-bar": { borderRadius: "999px", bgcolor: "progress.main" },
                      }}
                    />
                  </ButtonBase>
                ) : (
                  <_ProgressSkill node={node} />
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}

const stateLabels: Record<PracticalProgressState, string> = {
  notShown: "Nicht gezeigt",
  shown: "Gezeigt",
  learned: "Gelernt",
  mastered: "Gekonnt",
};
const steps = ["shown", "learned", "mastered"] as const;

function _ProgressSkill(props: { node: PracticalProgressSkill }) {
  const { node } = props;
  const activeIndex = steps.findIndex((state) => state === node.state);
  const completedSteps = activeIndex + 1;
  const fillWidth =
    completedSteps === 0 ? "0%" : completedSteps === 3 ? "100%" : `calc(${(completedSteps / 3) * 100}% + 8px)`;

  return (
    <Box sx={{ px: 2, py: 1.5, border: 1, borderColor: "divider", borderRadius: 2, overflowWrap: "anywhere" }}>
      <Typography component="h3" variant="body2" fontWeight="bold">
        {node.title}
      </Typography>
      {node.description && (
        <Typography variant="body2" color="text.secondary">
          {node.description}
        </Typography>
      )}
      <Box
        role="progressbar"
        aria-label={`Lernstand: ${node.title}`}
        aria-valuemin={0}
        aria-valuemax={3}
        aria-valuenow={completedSteps}
        aria-valuetext={stateLabels[node.state]}
        sx={{
          display: "flex",
          position: "relative",
          height: 28,
          mt: 1,
          borderRadius: "999px",
          overflow: "hidden",
          bgcolor: "action.disabledBackground",
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: fillWidth,
            bgcolor: "progress.main",
            clipPath:
              completedSteps > 0 && completedSteps < 3
                ? "polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)"
                : undefined,
          }}
        />
        {steps.map((state, index) => (
          <Box
            key={state}
            aria-hidden="true"
            sx={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              textAlign: "center",
              px: 0.5,
              fontSize: 12,
              lineHeight: "28px",
              whiteSpace: "nowrap",
              color: index <= activeIndex ? "common.white" : "text.secondary",
            }}
          >
            {stateLabels[state]}
          </Box>
        ))}
        {[1, 2].map((step) => (
          <Box
            key={step}
            component="svg"
            viewBox="0 0 8 28"
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: 0,
              left: `${(step / 3) * 100}%`,
              width: 8,
              height: "100%",
              color: "background.paper",
              overflow: "visible",
            }}
          >
            <polyline points="0,0 8,14 0,28" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </Box>
        ))}
      </Box>
      {(node.state === "notShown" || node.link) && (
        <Stack direction="row" alignItems="center" sx={{ mt: 1, gap: 2, flexWrap: "wrap" }}>
          {node.state === "notShown" && (
            <Typography variant="caption" color="text.secondary">
              Nicht gezeigt
            </Typography>
          )}
          {node.link && (
            <Button
              href={node.link}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={{ py: 0, fontSize: 12 }}
            >
              {node.linkLabel ?? "Informationen"}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
